import { _genHash } from "./_genHash";

/**
 * Guarantee every widget in a report config carries a unique, stable `id`.
 *
 * Widgets have been minted with an `id` (`_genHash(4)`) at creation for a while,
 * but reports authored before that may hold id-less widgets. Since the report now
 * tracks the under-edit widget (and, in collab, locks + Y.Doc writes) by that id,
 * an id-less widget would be untrackable. Backfilling as the server config enters
 * local state closes that gap and self-heals on the next save. Mirrors the
 * server-side backfill in okf-sub's loadReportDocument.
 *
 * Returns a new array only when a backfill was needed; otherwise the input is
 * returned unchanged (stable reference — safe to call in a seed effect).
 */
export const ensureWidgetIds = (config) => {
  if (!Array.isArray(config) || config.length === 0) return config;

  const taken = new Set(config.map((w) => w && w.id).filter(Boolean));
  if (taken.size === config.length) return config; // every widget already has a unique id

  const mint = () => {
    let id;
    do {
      id = _genHash(4);
    } while (taken.has(id));
    taken.add(id);
    return id;
  };

  return config.map((w) => (w && w.id ? w : { ...(w || {}), id: mint() }));
};
